import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../../api/api.js";
import { login } from "../../../store/authSlice";
import AuthLayout from "../../../components/organisms/Layout/Authlayout";
import { heyhireBlackIcon } from "../../../assets/images.js";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { toast } from "react-hot-toast";
import LanguageSwitcher from "../../../components/atoms/LanguageSwitcherDropdown/LanguageSwitcher.jsx";

const Login = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);

  useEffect(() => {
    document.title = t("title.heyhire-portal-login");
    return () => {
      setEmail("");
      setPassword("");
      setLoading(false);
      setValidationErrors(null);
      setErrorMessage("");
    };
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationErrors(null);
    setErrorMessage("");

    api
      .post("business/login", {
        email: email,
        password: password,
      })
      .then((response) => {
        localStorage.setItem("token", response.data.token);
        if (response.data.jwtToken) {
          localStorage.setItem("jwtToken", response.data.jwtToken);
        }
        localStorage.setItem("userData", JSON.stringify(response.data));
        dispatch(login(response.data));
        navigate("/dashboard");
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          if (error.response.data.message === "block") {
            setErrorMessage(t("login.location-blocked"));
          } else {
            if (
              (error.response.data.errors.email &&
                error.response.data.errors.email[0] ===
                t("login.error-message-check-email")) ||
              (error.response.data.errors.password &&
                error.response.data.errors.password[0] ===
                t("login.error-message-check-password"))
            ) {
              setErrorMessage(t("login.error-message"));
            } else {
              setValidationErrors(error.response.data.errors);
            }
          }
        } else {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .then(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (event) => {
    const target = event.target;
    const value = target?.value;
    const name = target?.name;
    if (name === "email") {
      setEmail(value);
    } else if (name === "password") {
      setPassword(value);
    }
  };

  return (
    <AuthLayout loginpage title={t("login.sign-in-account")}>
      <div className="flex flex-col min-h-screen">
        <div className="absolute top-4 right-6 z-10">
          <LanguageSwitcher />
        </div>
        <div className="flex flex-1 flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            {/* Logo and Title */}
            <div className="text-center mb-8">
              <img
                className="mx-auto h-12 w-auto"
                src={heyhireBlackIcon}
                alt="HeyHire"
              />
              <h2 className="mt-8 text-center text-3xl font-bold tracking-tight text-gray-900">
                {t("login.sign-in-account")}
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                Sign in to your account to continue
              </p>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-sm font-medium text-red-800 text-center">
                  {errorMessage}
                </p>
              </div>
            )}

            {/* Form Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
              <form className="space-y-6" onSubmit={handleLogin}>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t("common.email")}
                  </label>
                  <div className="relative">
                    <input
                      className={`block w-full rounded-lg border ${validationErrors?.email
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                        } px-4 py-2.5 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 sm:text-sm transition-colors`}
                      name="email"
                      type="email"
                      value={email}
                      placeholder={t("login.email-input-placeholder")}
                      onChange={handleInputChange}
                    />
                    {validationErrors?.email && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ExclamationCircleIcon
                          aria-hidden="true"
                          className="h-5 w-5 text-red-500"
                        />
                      </div>
                    )}
                  </div>
                  {validationErrors?.email && (
                    <p className="mt-2 text-sm text-red-600">
                      {validationErrors?.email[0]}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t("common.password")}
                  </label>
                  <div className="relative">
                    <input
                      className={`block w-full rounded-lg border ${validationErrors?.password
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                        } px-4 py-2.5 pr-10 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 sm:text-sm transition-colors`}
                      name="password"
                      type={passwordVisible ? "text" : "password"}
                      value={password}
                      placeholder={t("login.password-input-placeholder")}
                      onChange={handleInputChange}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                    >
                      <i
                        className={`far ${passwordVisible ? "fa-eye-slash" : "fa-eye"
                          } text-sm`}
                      />
                    </button>
                  </div>
                  {validationErrors?.password && (
                    <p className="mt-2 text-sm text-red-600">
                      {validationErrors?.password[0]}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
                  >
                    {t("login.forgot-password")}?
                  </Link>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full justify-center items-center rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <i className="fad fa-circle-notch fa-spin mr-2" />
                        {t("login.sign-in")}...
                      </>
                    ) : (
                      t("login.sign-in")
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;
